import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  TextField,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Build as BuildIcon,
  Assessment as AssessmentIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { PermissionsService } from '../services/permissionsService';
import { UserRole } from '../types';
import timezoneService from '../services/timezoneService';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  permissions: string[];
  createdAt: string;
  isActive: boolean;
}

interface PermissionGroup {
  icon: React.ReactNode;
  permissions: Array<{
    key: string;
    label: string;
    description: string;
  }>;
}

export const AdminSettings: React.FC = () => {
  const { state: authState, updateUser, refreshProfile } = useAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [newRole, setNewRole] = useState<UserRole>('member');
  const [customPermissions, setCustomPermissions] = useState<string[]>([]);
  const [useCustomPermissions, setUseCustomPermissions] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('member');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const userPermissions = authState.user?.permissions || [];
  const canManageUsers = PermissionsService.hasPermission(userPermissions, 'manage_users');
  const canManageSettings = PermissionsService.hasPermission(userPermissions, 'manage_settings');

  // Permission groups for better UX
  const permissionGroups: Record<string, PermissionGroup> = {
    'View Permissions': {
      icon: <VisibilityIcon />,
      permissions: [
        { key: 'view_home', label: 'View Home', description: 'Access to dashboard and home page' },
        { key: 'view_time', label: 'View Time Tracking', description: 'Access to time tracking features' },
        { key: 'view_projects', label: 'View Projects', description: 'Access to project listings and details' },
        { key: 'view_team', label: 'View Team', description: 'Access to team member information' },
        { key: 'view_chat', label: 'View Chat', description: 'Access to team chat features' },
        { key: 'view_expenses', label: 'View Expenses', description: 'Access to expense tracking' },
        { key: 'view_reports', label: 'View Reports', description: 'Access to reporting features' },
        { key: 'view_invoices', label: 'View Invoices', description: 'Access to invoice management' },
        { key: 'view_estimates', label: 'View Estimates', description: 'Access to project estimates' },
      ]
    },
    'Management Permissions': {
      icon: <BuildIcon />,
      permissions: [
        { key: 'manage_users', label: 'Manage Users', description: 'Add, edit, and remove team members' },
        { key: 'manage_projects', label: 'Manage Projects', description: 'Create and modify projects' },
        { key: 'manage_settings', label: 'Manage Settings', description: 'Access to system settings' },
        { key: 'manage_team_permissions', label: 'Manage Team Permissions', description: 'Modify team member permissions' },
      ]
    },
    'Analytics & AI': {
      icon: <AssessmentIcon />,
      permissions: [
        { key: 'view_ai_analytics', label: 'View AI Analytics', description: 'Access to AI-powered analytics' },
        { key: 'view_ai_settings', label: 'View AI Settings', description: 'Access to AI configuration' },
        { key: 'view_screenshot_history', label: 'View Screenshot History', description: 'Access to screenshot tracking' },
        { key: 'view_team_analytics', label: 'View Team Analytics', description: 'Access to team performance analytics' },
      ]
    },
    'Admin Features': {
      icon: <AdminIcon />,
      permissions: [
        { key: 'view_owner_dashboard', label: 'Owner Dashboard', description: 'Access to owner-level dashboard features' },
        { key: 'view_manage', label: 'View Management Panel', description: 'Access to advanced management features' },
      ]
    }
  };

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    try {
      console.log('🔄 AdminSettings: Loading team members...');
      const response = await apiService.getTeamMembers();
      console.log('🔄 AdminSettings: Raw team members response:', response);
      
      // Parse permissions if they come as strings
      const processedMembers = (response || []).map(member => {
        return {
          ...member,
          permissions: typeof member.permissions === 'string' 
            ? JSON.parse(member.permissions) 
            : member.permissions || []
        };
      });
      
      console.log('🔄 AdminSettings: Processed team members:', processedMembers);
      setTeamMembers(processedMembers);
    } catch (error) {
      console.error('Error loading team members:', error);
      setError('Failed to load team members');
    }
  };

  const handleEditRole = (member: TeamMember) => {
    setSelectedMember(member);
    setNewRole(member.role);
    setCustomPermissions([...member.permissions]);
    setUseCustomPermissions(false);
    setEditDialogOpen(true);
  };

  const handlePermissionToggle = (permission: string) => {
    setCustomPermissions(prev => {
      if (prev.includes(permission)) {
        return prev.filter(p => p !== permission);
      } else {
        return [...prev, permission];
      }
    });
  };

  const handleSaveRole = async () => {
    if (!selectedMember) return;

    try {
      // Send custom permissions if using custom permissions, otherwise let backend use defaults
      const permissionsToSend = useCustomPermissions ? customPermissions : undefined;
      
      await apiService.updateUserRole(selectedMember.id, newRole, permissionsToSend);
      
      setSuccess(`Successfully updated ${selectedMember.name}'s role to ${PermissionsService.getRoleDisplayName(newRole)}`);
      setEditDialogOpen(false);
      setSelectedMember(null);
      
      // If we updated the current user's role, refresh their profile in AuthContext
      if (selectedMember.id === authState.user?.id) {
        try {
          await refreshProfile();
          console.log('🔄 AdminSettings: Refreshed current user profile after role change');
        } catch (profileError) {
          console.error('🔄 AdminSettings: Failed to refresh current user profile:', profileError);
        }
      }
      
      // Reload team members immediately to show updated data
      await loadTeamMembers();
    } catch (error) {
      console.error('Error updating user role:', error);
      setError(`Failed to update user role: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail || !inviteName) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      // For now, we'll create a user directly since we don't have an invite system
      // In a real implementation, you'd send an invitation email
      const userData = {
        name: inviteName,
        email: inviteEmail,
        role: inviteRole,
        password: 'temp123' // In real implementation, user would set this via invite link
      };

      // This would be an invite API call in a real system
      // await apiService.inviteUser(userData);
      
      setSuccess(`Invitation sent to ${inviteName} (${inviteEmail}) as ${PermissionsService.getRoleDisplayName(inviteRole)}`);
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('member');
      
      // Reload team members to show any changes
      await loadTeamMembers();
    } catch (error) {
      console.error('Error inviting user:', error);
      setError(`Failed to invite user: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiService.deleteUser(userId);
      setSuccess(`Successfully deleted ${userName}`);
      loadTeamMembers(); // Reload to get updated data
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user');
    }
  };

  const getRoleChipColor = (role: UserRole) => {
    const normalizedRole = role.toLowerCase();
    
    switch (normalizedRole) {
      case 'owner':
        return 'error';
      case 'administrator':
      case 'admin':
        return 'warning';
      case 'tester':
        return 'info';
      case 'member':
        return 'success';
      default:
        return 'default';
    }
  };

  const getAllPermissions = () => {
    return Object.values(permissionGroups).flatMap(group => 
      group.permissions.map(p => p.key)
    );
  };

  const getPermissionLabel = (permissionKey: string) => {
    for (const group of Object.values(permissionGroups)) {
      const permission = group.permissions.find(p => p.key === permissionKey);
      if (permission) return permission.label;
    }
    return permissionKey;
  };

  if (!canManageUsers && !canManageSettings) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to access admin settings.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon />
        Admin Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* System Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon />
            System Overview
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1, minWidth: 120, flex: 1 }}>
              <Typography variant="h4" color="primary.contrastText">
                {teamMembers.length}
              </Typography>
              <Typography variant="body2" color="primary.contrastText">
                Total Users
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1, minWidth: 120, flex: 1 }}>
              <Typography variant="h4" color="success.contrastText">
                {teamMembers.filter(m => m.isActive).length}
              </Typography>
              <Typography variant="body2" color="success.contrastText">
                Active Users
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 1, minWidth: 120, flex: 1 }}>
              <Typography variant="h4" color="warning.contrastText">
                {teamMembers.filter(m => m.role.toLowerCase() === 'admin' || m.role.toLowerCase() === 'administrator').length}
              </Typography>
              <Typography variant="body2" color="warning.contrastText">
                Administrators
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'error.light', borderRadius: 1, minWidth: 120, flex: 1 }}>
              <Typography variant="h4" color="error.contrastText">
                {teamMembers.filter(m => m.role.toLowerCase() === 'owner').length}
              </Typography>
              <Typography variant="body2" color="error.contrastText">
                Owners
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* User Management */}
      {canManageUsers && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon />
                User Management
              </Typography>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setInviteDialogOpen(true)}
              >
                Invite User
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>{member.name}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={PermissionsService.getRoleDisplayName(member.role)}
                          color={getRoleChipColor(member.role)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip 
                          title={
                            <Box>
                              {member.permissions.map(permission => (
                                <Typography key={permission} variant="body2">
                                  • {getPermissionLabel(permission)}
                                </Typography>
                              ))}
                            </Box>
                          }
                        >
                          <Chip
                            label={`${member.permissions.length} permissions`}
                            variant="outlined"
                            size="small"
                            color="primary"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {timezoneService.formatUserDate(
                          timezoneService.convertUTCToUserTime(member.createdAt)
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={member.isActive ? 'Active' : 'Inactive'}
                          color={member.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEditRole(member)}
                          disabled={member.role === 'owner' || member.id === authState.user?.id}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteUser(member.id, member.name)}
                          disabled={member.role === 'owner' || member.id === authState.user?.id}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Enhanced Edit Role Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit User Role & Permissions</DialogTitle>
        <DialogContent>
          {selectedMember && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Editing role for: <strong>{selectedMember.name}</strong>
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newRole}
                  label="Role"
                  onChange={(e) => {
                    const role = e.target.value as UserRole;
                    setNewRole(role);
                    if (!useCustomPermissions) {
                      setCustomPermissions(PermissionsService.getDefaultPermissions(role));
                    }
                  }}
                >
                  <MenuItem value="member">Member</MenuItem>
                  <MenuItem value="tester">Tester</MenuItem>
                  <MenuItem value="administrator">Administrator</MenuItem>
                  {authState.user?.role === 'owner' && (
                    <MenuItem value="owner">Owner</MenuItem>
                  )}
                </Select>
              </FormControl>

              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useCustomPermissions}
                      onChange={(e) => setUseCustomPermissions(e.target.checked)}
                    />
                  }
                  label="Use custom permissions"
                />
              </Box>

              {useCustomPermissions ? (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Custom Permissions
                  </Typography>
                  {Object.entries(permissionGroups).map(([groupName, group]) => (
                    <Accordion key={groupName} defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {group.icon}
                          <Typography variant="subtitle1">{groupName}</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <FormGroup>
                          {group.permissions.map((permission) => (
                            <FormControlLabel
                              key={permission.key}
                              control={
                                <Checkbox
                                  checked={customPermissions.includes(permission.key)}
                                  onChange={() => handlePermissionToggle(permission.key)}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {permission.label}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {permission.description}
                                  </Typography>
                                </Box>
                              }
                            />
                          ))}
                        </FormGroup>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              ) : (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Default Permissions for {PermissionsService.getRoleDisplayName(newRole)}
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                    {PermissionsService.getDefaultPermissions(newRole).map((permission) => (
                      <Chip
                        key={permission}
                        label={getPermissionLabel(permission)}
                        size="small"
                        sx={{ m: 0.5 }}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveRole} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite New User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={inviteRole}
                label="Role"
                onChange={(e) => setInviteRole(e.target.value as UserRole)}
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="tester">Tester</MenuItem>
                <MenuItem value="administrator">Administrator</MenuItem>
                {authState.user?.role === 'owner' && (
                  <MenuItem value="owner">Owner</MenuItem>
                )}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              <strong>Permissions for {PermissionsService.getRoleDisplayName(inviteRole)}:</strong>
              <br />
              {PermissionsService.getDefaultPermissions(inviteRole).map(permission => 
                getPermissionLabel(permission)
              ).join(', ')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleInviteUser} variant="contained">
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
